import { useEffect, useRef } from 'react';
import {
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorState,
  Row,
  Tag,
  type Column,
} from '@sparkery/ui';
import type { MovementKind, StatementMovement } from '@sparkery/contracts';
import { money } from '../../lib/report.tsx';
import { periodLabel, useStatementDetail } from '../../lib/statements.ts';

/**
 * One statement, opened.
 *
 * This is the screen that answers "why is this £1,605.60". The statement's own totals
 * are a summary of these rows, so the panel puts the arithmetic and the rows that make
 * it up on the same screen — and states that they add up, because a partner checking a
 * figure should not have to take it on trust.
 *
 * The opening balance is shown apart from the movements, because it is the one line
 * with no order behind it: it came from an earlier statement.
 */
export function StatementPanel({ statementId }: { statementId: string }) {
  const detail = useStatementDetail(statementId);
  const data = detail.data;
  const panel = useRef<HTMLDivElement>(null);

  // Brought into view when the selection changes. The panel sits below the list, so
  // clicking a row near the bottom would otherwise open something off-screen and read
  // as nothing having happened. `nearest` rather than `start`, so a panel already
  // visible does not jump.
  useEffect(() => {
    panel.current?.scrollIntoView({ block: 'nearest' });
  }, [statementId]);

  if (detail.isError) {
    return (
      <ErrorState
        title="Could not open that statement"
        {...(detail.error instanceof Error ? { detail: detail.error.message } : {})}
        onRetry={() => void detail.refetch()}
      />
    );
  }

  const statement = data?.statement;

  return (
    <Card pad="0">
      {/* Its own wrapper, not a TableCard: this one carries the ref the panel
          scrolls into view. The class is the shared one. */}
      <div className="tableCardHead" ref={panel}>
        <CardHeader
          title={statement ? `${periodLabel(statement.period)} — how it was made up` : 'Opening…'}
          meta={
            statement
              ? [
                  statement.partnerName,
                  `${statement.entryCount} ${statement.entryCount === 1 ? 'movement' : 'movements'}`,
                ].join(' · ')
              : undefined
          }
        />
      </div>

      {statement ? (
        <div className="statementBreakdown">
          <Line label="Brought forward" amount={statement.openingBalance.amount} muted />
          <Line label="Commission earned" amount={statement.earned.amount} />
          {statement.adjusted.amount === '0.00' ? null : (
            <Line label="Refunded before payout" amount={statement.adjusted.amount} />
          )}
          {statement.recovered.amount === '0.00' ? null : (
            <Line label="Recovered from earlier payouts" amount={statement.recovered.amount} />
          )}
          <Line label="Payable" amount={statement.payable.amount} strong />
          {statement.carriedForward.amount === '0.00' ? null : (
            <Line label="Carried into next month" amount={statement.carriedForward.amount} muted />
          )}
        </div>
      ) : null}

      <DataTable
        caption="The movements this statement settled"
        columns={columns}
        rows={data?.movements ?? []}
        rowKey={(row) => row.id}
        loading={detail.isPending}
        empty={
          <EmptyState
            title="No movements"
            body="Only a balance brought forward from an earlier month."
          />
        }
      />
    </Card>
  );
}

function Line({
  label,
  amount,
  strong,
  muted,
}: {
  label: string;
  amount: string;
  strong?: boolean;
  muted?: boolean;
}) {
  return (
    <Row gap="12px" className={muted ? 'statementLine statementLineMuted' : 'statementLine'}>
      <span className="statementLineLabel">{label}</span>
      <span className="statementLineAmount">
        {strong ? <strong>{money(amount)}</strong> : money(amount)}
      </span>
    </Row>
  );
}

/**
 * What each kind means, in the words a partner reads.
 *
 * `recovery` gets the strongest wording because it is the only line that refers to a
 * month they have already been paid for — the one they will query.
 */
const KIND: Record<MovementKind, { label: string; tone: 'confirmed' | 'refunded' | 'neutral' }> = {
  commission: { label: 'Commission', tone: 'confirmed' },
  refund: { label: 'Refunded', tone: 'refunded' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
  recovery: { label: 'Recovered', tone: 'refunded' },
};

const columns: Column<StatementMovement>[] = [
  {
    id: 'order',
    header: 'Order',
    cell: (row) => (
      <span className="statementOrder">
        <strong>{row.orderName}</strong>
        <span className="statementCustomer">{row.customerLabel ?? '—'}</span>
      </span>
    ),
  },
  {
    id: 'kind',
    header: 'What',
    cell: (row) => <Tag tone={KIND[row.kind].tone}>{KIND[row.kind].label}</Tag>,
  },
  {
    id: 'basis',
    header: 'Order value',
    numeric: true,
    secondary: true,
    // The value the commission was computed on, frozen at accrual — not the order as it
    // stands now. Today's total beside a historic figure reads as an error.
    cell: (row) => money(row.orderValue.amount),
  },
  {
    id: 'rate',
    header: 'Rate',
    numeric: true,
    secondary: true,
    cell: (row) => `${Number(row.rate)}%`,
  },
  {
    id: 'amount',
    header: 'Commission',
    numeric: true,
    cell: (row) => (
      <span className={row.amount.amount.startsWith('-') ? 'statementNegative' : undefined}>
        {money(row.amount.amount)}
      </span>
    ),
  },
  {
    id: 'matured',
    header: 'Became payable',
    numeric: true,
    secondary: true,
    // The date that decides which statement a movement lands in, which is the question
    // this column exists to answer.
    cell: (row) =>
      new Date(row.maturesAt).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
      }),
  },
];
